/**
 * @file Search.tsx
 * @description This file defines a debounced search input component with a clear button.
 * It is a forward-ref component that can be used in forms.
 */
import { debounce } from "lodash-es";
import { forwardRef, useEffect, useMemo, useState } from "react"
import { FaSearch, FaTimes } from "react-icons/fa";
import { IconType } from "react-icons";

/**
 * Props for the SearchInput component.
 * Extends label attributes but omits `onChange` to provide a custom implementation.
 */
export interface SearchInputProps extends Omit<React.LabelHTMLAttributes<HTMLLabelElement>, 'onChange'> {
    onChange?: (value: string) => void // Custom change handler that returns the new value.
    onClear?: () => void // Handler for when the input is cleared.
    timeout?: number // Debounce timeout in milliseconds.
    value?: string // The value of the search input.
    closeActive?: boolean // Controls the visibility of the clear icon.
    closeOutside?: boolean // Determines if the clear button is outside the input field.
    cleanIcon?: IconType // Custom icon for the clear button.
}

/**
 * A debounced search input component.
 * It wraps a text input with search and clear icons, and debounces the `onChange` event.
 */
export const SearchInput = forwardRef<HTMLLabelElement, SearchInputProps>(({
    value = "",
    onChange,
    onClear,
    onBlur,
    className,
    timeout = 1000,
    closeActive,
    closeOutside,
    cleanIcon: CleanIcon = FaTimes,
    ...rest
}, ref) => {
    // Internal state for the search input value.
    const [search, setSearch] = useState(value)

    // Debounced onChange handler.
    const debouncedOnChange = useMemo(() => debounce((value: string) => {
        onChange?.(value)
    }, timeout), [onChange, timeout])

    // Effect to trigger the debounced onChange when the internal search state changes.
    useEffect(() => {
        debouncedOnChange.cancel()
        debouncedOnChange(search)
    }, [search, debouncedOnChange])

    // Effect to sync the internal state with the external value prop.
    useEffect(() => {
        setSearch(value)
    }, [value])

    const handleClear = () => {
        setSearch('')
        // The timeout ensures that the debounced `onChange` with an empty string is fired
        // before the optional onClear handler.
        setTimeout(() => onClear?.(), timeout)
    }

    return <>
        <label ref={ref} className={`grow input flex items-center gap-2 ${className ?? ''}`} {...rest}>
            <FaSearch />
            <input type="text" className="grow w-full bg-transparent" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} onBlur={onBlur as unknown as React.FocusEventHandler<HTMLInputElement>} />
            {!closeOutside && (search != '' || closeActive) && <CleanIcon className="cursor-pointer" onClick={handleClear} />}
        </label>
        {closeOutside && <button
            className="btn btn-ghost join-item"
            onClick={handleClear}
        >
            <FaTimes />
        </button>}
    </>
})